import React from 'react';
import { ShieldCheck } from 'lucide-react';

const LAST_UPDATED = 'Marzo 2026';

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h2 className="text-lg font-semibold text-gray-900 mb-2">{title}</h2>
    <div className="text-sm text-gray-600 leading-relaxed space-y-2">{children}</div>
  </div>
);

const Privacy: React.FC = () => {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-8 w-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-gray-900">Política de Privacidad</h1>
        </div>
        <p className="text-gray-500 text-sm">Última actualización: {LAST_UPDATED}</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <Section title="1. Información que recopilamos">
          <p>
            Kessan recopila la información necesaria para el funcionamiento de la plataforma de gestión de gastos:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Datos de identificación: nombre, apellido, correo electrónico</li>
            <li>Datos de la empresa: razón social, RUT, nombre de fantasía, correo y teléfono de contacto</li>
            <li>Datos operativos: proyectos, gastos, categorías, comprobantes adjuntos</li>
            <li>Datos de acceso: registros de inicio de sesión y actividad en la plataforma</li>
          </ul>
        </Section>

        <Section title="2. Uso de la información">
          <p>Utilizamos la información recopilada exclusivamente para:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Proveer y mantener el servicio de gestión de gastos</li>
            <li>Autenticar usuarios y gestionar el acceso a la plataforma</li>
            <li>Generar reportes y estadísticas para la empresa contratante</li>
            <li>Enviar comunicaciones transaccionales (credenciales de acceso, notificaciones del sistema)</li>
            <li>Mejorar la calidad y funcionalidad del servicio</li>
          </ul>
        </Section>

        <Section title="3. Almacenamiento y seguridad">
          <p>
            Los datos se almacenan en infraestructura de Amazon Web Services (AWS) en la región de Estados Unidos (us-east-1).
            Implementamos medidas de seguridad que incluyen:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Cifrado de datos en tránsito mediante HTTPS/TLS</li>
            <li>Cifrado de datos en reposo en bases de datos y almacenamiento</li>
            <li>Autenticación segura mediante Amazon Cognito</li>
            <li>Aislamiento de datos entre empresas (multi-tenancy seguro)</li>
            <li>Registro de auditoría de operaciones sensibles</li>
          </ul>
        </Section>

        <Section title="4. Compartición de datos">
          <p>
            No vendemos, alquilamos ni compartimos información personal con terceros con fines comerciales.
            Los datos solo son accesibles por:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Los usuarios autorizados de la empresa contratante, según su rol y permisos</li>
            <li>El equipo técnico de Kessan, exclusivamente para soporte y mantenimiento</li>
            <li>Proveedores de infraestructura (AWS), bajo sus propias políticas de seguridad y cumplimiento</li>
          </ul>
        </Section>

        <Section title="5. Retención de datos">
          <p>
            Los datos se mantienen mientras la empresa tenga una suscripción activa.
            En caso de cancelación, los datos se conservan por un período de 90 días
            antes de ser eliminados permanentemente, permitiendo la reactivación del servicio si es necesario.
          </p>
        </Section>

        <Section title="6. Derechos del usuario">
          <p>Los usuarios tienen derecho a:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Acceder a su información personal almacenada en la plataforma</li>
            <li>Solicitar la corrección de datos inexactos</li>
            <li>Solicitar la eliminación de su cuenta y datos asociados</li>
            <li>Exportar sus datos en formatos estándar</li>
          </ul>
          <p>
            Para ejercer estos derechos, contacta al administrador de tu empresa o al equipo de Kessan.
          </p>
        </Section>

        <Section title="7. Cambios en esta política">
          <p>
            Nos reservamos el derecho de actualizar esta política de privacidad.
            Los cambios significativos serán notificados a través de la plataforma.
            El uso continuado del servicio después de los cambios constituye la aceptación de la política actualizada.
          </p>
        </Section>
      </div>
    </div>
  );
};

export default Privacy;
